import { Stack, Tooltip, Typography, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import AdminAPI from '@/api/TreedisAdminDAO';
import { RatingImage } from '@/components/navigation/NavigationIntroScreen/components/Popup/styles';
import DeviceManager from '@/components/util/deviceManager';
import { useNavigationStore } from '../../../../../store';
import { CrossIcon } from '@/components/navigation/NavigationIntroScreen/components/Popup/Icons';
import { mobileCloseButtonStyle } from '@/components/navigation/search-bar/styles';

type Props = {
  children: any;
  tour?: any;
  target?: any;
  actionPathFinished?: boolean;
  ratingEnabled?: boolean;
  isShare?: boolean;
  tooltipContainerRef?: any;
  rtl?: boolean;
  setMobilePosition: (val: number) => void;
};
const ratings = [
  { value: 1, icon: '/navigation/emoji_1.svg', iconHover: '/navigation/emoji_1_hover.svg' },
  { value: 2, icon: '/navigation/emoji_2.svg', iconHover: '/navigation/emoji_2_hover.svg' },
  { value: 3, icon: '/navigation/emoji_3.svg', iconHover: '/navigation/emoji_3_hover.svg' },
  { value: 4, icon: '/navigation/emoji_4.svg', iconHover: '/navigation/emoji_4_hover.svg' },
  { value: 5, icon: '/navigation/emoji_5.svg', iconHover: '/navigation/emoji_5_hover.svg' },
];

export const RatingTooltip = ({
  children,
  tour,
  target,
  actionPathFinished,
  ratingEnabled,
  isShare,
  tooltipContainerRef,
  rtl,
  setMobilePosition,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const rect = tooltipContainerRef?.current?.getBoundingClientRect();
  const translation = useNavigationStore((state) => state.navigationTranslation);
  const isMobile = DeviceManager.isMobile();

  useEffect(() => {
    let openTimeoutId;
    let closeTimeoutId;
    let autoCloseIfNoRatingTimeoutId;

    if (actionPathFinished && ratingEnabled) {
      // Delay opening the popup
      openTimeoutId = setTimeout(
        () => {
          setOpen(true);

          // If no rating after 5s, close automatically
          if (!selectedRating) {
            autoCloseIfNoRatingTimeoutId = setTimeout(() => {
              setOpen(false);
            }, 5000);
          }
        },
        isShare ? 500 : 3000,
      );

      // If rating is already chosen, we still close after 3s
      if (selectedRating) {
        closeTimeoutId = setTimeout(() => {
          setOpen(false);
        }, 3000);
      }
    }

    return () => {
      clearTimeout(openTimeoutId);
      clearTimeout(closeTimeoutId);
      clearTimeout(autoCloseIfNoRatingTimeoutId);
    };
  }, [actionPathFinished, isShare, ratingEnabled, selectedRating]);

  useEffect(() => {
    if (actionPathFinished && ratingEnabled && isMobile) {
      setMobilePosition(1);
    }
  }, [actionPathFinished, isMobile, ratingEnabled, setMobilePosition]);

  const handleSelectRating = async (rating) => {
    if (selectedRating) return;
    setSelectedRating(rating);

    const dataToSave =
      typeof target.id === 'number'
        ? {
            rating: rating.value,
            tourId: tour.id,
            targetId: target.id,
            targetTitle:
              target.title || target.name || target.tag.title || target.tagId || target.tagSid,
          }
        : {
            rating: rating.value,
            tourId: tour.id,
            sweepId: target.id,
            targetTitle: target.title || target.name,
          };
    await AdminAPI.saveNavigationRating(dataToSave);
  };

  return (
    <Tooltip
      open={open}
      arrow={!DeviceManager.isMobile()}
      placement={DeviceManager.isMobile() ? 'top' : 'right'}
      componentsProps={{
        arrow: { sx: { color: '#fff' } },
        tooltip: {
          sx: {
            background: '#fff',
            p: '12px',
            borderRadius: '8px',
            margin: 0,
            marginLeft: { xs: 0, sm: isShare ? '50px !important' : '14px !important' },
            marginTop: { xs: 0, sm: isShare ? '45px' : '' },
            maxWidth: { xs: '100%', sm: '300px' },
          },
        },
      }}
      PopperProps={{
        sx: DeviceManager.isMobile()
          ? {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              width: 'calc(100vw - 28px)',
              borderRadius: '8px',
            }
          : {},
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: DeviceManager.isMobile() && rect?.top ? [0, rect.top - 150] : [0, 20],
            },
          },
        ],
      }}
      title={
        <Stack>
          <Button
            sx={mobileCloseButtonStyle}
            onClick={() => {
              setOpen(false);
            }}
          >
            <CrossIcon style={{}} />
          </Button>
          <Stack sx={{ padding: '15px 10px' }}>
            <Typography
              sx={{
                color: '#000',
                textAlign: 'center',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '24px',
                direction: rtl ? 'rtl' : 'ltr',
              }}
            >
              {!selectedRating
                ? `${translation.navDidYouLike || 'Did you like the navigation?'}`
                : `${translation.navThanksForVote || 'Thank you!'}`}
            </Typography>
            <Stack direction="row" gap="11.36px" pt="16px">
              {ratings.map((rating) => (
                <RatingImage
                  onMouseEnter={(e) => {
                    if (!selectedRating || selectedRating.value !== rating.value) {
                      e.currentTarget.src = rating.iconHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedRating || selectedRating.value !== rating.value) {
                      e.currentTarget.src = rating.icon;
                    }
                  }}
                  onClick={() => handleSelectRating(rating)}
                  key={rating.value}
                  width="41px"
                  height="41px"
                  src={selectedRating?.value === rating.value ? rating.iconHover : rating.icon}
                  alt={`rating-${rating.value}`}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      }
    >
      {children}
    </Tooltip>
  );
};
